import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const TEXT = {
  es: {
    title: 'Eliminar cuenta',
    description: 'Se borrarán para siempre todos los datos de tu empresa: productos, pedidos, conexiones con tiendas, automatizaciones y los usuarios del equipo. Si tienes una suscripción, se cancelará. No se puede deshacer.',
    open: 'Eliminar mi cuenta',
    password: 'Tu contraseña',
    confirmLabel: 'Escribe ELIMINAR para confirmar',
    word: 'ELIMINAR',
    submit: 'Eliminar definitivamente',
    deleting: 'Eliminando...',
    cancel: 'Cancelar',
    error: 'No se pudo eliminar la cuenta.',
    done: 'Tu cuenta se ha eliminado. Te hemos enviado un email de confirmación.',
  },
  en: {
    title: 'Delete account',
    description: 'All your company’s data will be permanently deleted: products, orders, store connections, automations and team users. If you have a subscription, it will be cancelled. This can’t be undone.',
    open: 'Delete my account',
    password: 'Your password',
    confirmLabel: 'Type DELETE to confirm',
    word: 'DELETE',
    submit: 'Delete permanently',
    deleting: 'Deleting...',
    cancel: 'Cancel',
    error: 'The account couldn’t be deleted.',
    done: 'Your account has been deleted. We’ve sent you a confirmation email.',
  },
  fr: {
    title: 'Supprimer le compte',
    description: 'Toutes les données de votre entreprise seront définitivement supprimées : produits, commandes, connexions aux boutiques, automatisations et utilisateurs de l’équipe. Si vous avez un abonnement, il sera annulé. Cette action est irréversible.',
    open: 'Supprimer mon compte',
    password: 'Votre mot de passe',
    confirmLabel: 'Tapez SUPPRIMER pour confirmer',
    word: 'SUPPRIMER',
    submit: 'Supprimer définitivement',
    deleting: 'Suppression...',
    cancel: 'Annuler',
    error: 'Le compte n’a pas pu être supprimé.',
    done: 'Votre compte a été supprimé. Nous vous avons envoyé un e-mail de confirmation.',
  },
};

function DeleteAccountCard({ token, onDeleted }) {
  const { i18n } = useTranslation();
  const tx = TEXT[(i18n.language || 'es').slice(0, 2)] || TEXT.es;
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const ready = password.length > 0 && confirm.trim().toUpperCase() === tx.word;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/v1/account/delete`,
        { current_password: password, confirm: confirm.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert(tx.done);
      onDeleted();
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      setError(typeof detail === 'string' ? detail : tx.error);
      setBusy(false);
    }
  };

  const input = 'w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm mb-3 focus:outline-none focus:border-red-500';

  return (
    <div className="bg-surface border border-[#7f1d1d] rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
      <p className="text-red-400 text-sm font-semibold mb-1">{tx.title}</p>
      <p className="text-muted text-xs mb-4">{tx.description}</p>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-red-400 text-sm px-4 py-2 rounded-lg border border-[#7f1d1d] cursor-pointer"
        >
          {tx.open}
        </button>
      ) : (
        <form onSubmit={submit}>
          <label className="block text-muted text-xs mb-1">{tx.password}</label>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
          <label className="block text-muted text-xs mb-1">{tx.confirmLabel}</label>
          <input type="text" autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} />
          {error && (
            <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-3">{error}</div>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!ready || busy}
              className={`text-sm font-semibold px-4 py-2 rounded-lg ${ready && !busy ? 'bg-red-600 text-white cursor-pointer' : 'bg-[#3f1d1d] text-red-300 opacity-60 cursor-not-allowed'}`}
            >
              {busy ? tx.deleting : tx.submit}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setPassword(''); setConfirm(''); setError(''); }}
              disabled={busy}
              className="text-muted text-sm px-4 py-2 rounded-lg border border-edge cursor-pointer"
            >
              {tx.cancel}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default DeleteAccountCard;
